import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'src/app/common/hooks/translation.hook';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import { useHistory } from 'react-router-dom';
import { useRequest } from 'ahooks';

import { fetchCurrentAgentInfo } from 'src/app/modules/Auth/network/authCrud';
import { last_30_days_rages } from 'src/app/common/components/list-filter/components/list-filter-date-radio-group/util/list-filter-date-radio.util';
import { globalStore } from 'src/app/common/helpers/global-store.util';
import { getLeadsMarketing } from '../../../network/insightCrud';
import { formatNumber } from '../../../util/insight.util';
import { RootState } from 'src/redux/store';
import { ListFilterConfig, ListFilterData } from 'src/app/common/components/list-filter/list-filter.component';
import { FilterComponentsEnum } from 'src/app/common/components/list-filter/filter-components.enum';
import { insightAction } from '../../../redux/insight.slice';

interface HookProps {
  // agent-code string, split by comma,
  // example: "00010001" or "00010001,00010002,00010003"
  agentCode?: string;
}

export const useMarketingLead = ({ agentCode }: HookProps) => {
  // i18n
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const defaultRange = last_30_days_rages();
  const filterData = useSelector<RootState>((state) => state.insight?.overviewMarketingFilter) as ListFilterData;

  const agentCodeList = agentCode || globalStore.getAgentCode();
  const defaultValues = {
    dateRange: {
      key: 'last_30_days',
      value: {
        startDate: defaultRange.startDate,
        endDate: defaultRange.endDate,
        label: t('last_30_days'),
      },
    },
  };
  const [filterValues, setFilterValues] = useState<ListFilterData>(filterData || defaultValues);
  const filterConfig: ListFilterConfig[] = [
    {
      title: t('insights_new_added_date'),
      items: [],
      key: 'dateRange',
      type: FilterComponentsEnum.RADIO_GROUP, // select date range
    },
  ];
  const onFilterConfirm = useCallback((filterData: ListFilterData) => {
    setFilterValues(filterData);
    dispatch(insightAction.setOverviewMarketingLeadFilter(filterData));
  }, []);

  // useRequest to wrap api request.
  const { data, error, loading, run, runAsync } = useRequest(
    () => {
      const body = {
        agentCode: agentCodeList,
        startDate: get(filterValues, 'dateRange.value.startDate') as string,
        endDate: get(filterValues, 'dateRange.value.endDate') as string,
      };
      return getLeadsMarketing(body).then((response) => {
        const { received, rejected, slaRatio, missedSla, acceptedTimeCount, acceptedTimeAvg } = response.data;
        const slaRatioFormat = formatNumber(slaRatio, 0);
        const result = [
          {
            value: formatNumber(received) ?? '-',
            label: t('received'),
          },
          {
            value: formatNumber(rejected) ?? '-',
            label: t('rejected'),
          },
          {
            value: formatNumber(acceptedTimeCount) ?? '-',
            label: t('marketing_lead_accepted'),
          },
          {
            value: `${formatNumber(acceptedTimeAvg, 0)} ${t('min')}`,
            label: t('marketing_lead_avr_time_to_accept'),
          },
          {
            value: `${isNaN(slaRatioFormat as any) ? '-' : slaRatioFormat + '%'}`,
            label: t('sla_ratio'),
          },
          {
            value: formatNumber(missedSla) ?? '-',
            label: t('missed_sla'),
          },
        ];
        return result;
      });
    },
    {
      manual: true,
    },
  );

  useEffect(() => {
    // manual call api request
    if (agentCodeList) {
      run();
    }
  }, [agentCodeList, filterValues]);

  const handlePress = useCallback(() => {
    history.push('./marketing-lead', { agentCode: agentCodeList });
  }, []);

  const defaultData = useMemo(() => {
    const result = [
      {
        value: '-',
        label: t('received'),
      },
      {
        value: '-',
        label: t('rejected'),
      },
      {
        value: '-',
        label: t('marketing_lead_accepted'),
      },
      {
        value: '-',
        label: t('marketing_lead_avr_time_to_accept'),
      },
      {
        value: '-',
        label: t('sla_ratio'),
      },
      {
        value: '-',
        label: t('missed_sla'),
      },
    ];
    return result;
  }, []);

  const tooltip = useMemo(() => {
    const tooltip = [
      {
        title: t('received'),
        content: t('market_des1'),
      },
      {
        title: t('rejected'),
        content: t('market_des2'),
      },
      {
        title: t('marketing_lead_accepted'),
        content: t('marketing_lead_accepted_des'),
      },
      {
        title: t('marketing_lead_avr_time_to_accept'),
        content: t('marketing_lead_avr_time_to_accept_des'),
      },
      {
        title: t('sla_ratio'),
        content: t('market_des3'),
      },
      {
        title: t('missed_sla'),
        content: t('market_des4'),
      },
    ];
    return tooltip;
  }, []);

  return {
    tooltip,
    data: loading ? defaultData : data || defaultData,
    filterValues,
    defaultValues,
    filterConfig,
    onFilterConfirm,
    handlePress,
  };
};
